<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import SalesAPI from 'dashboard/api/sales';

const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');

const isOpen = ref(false);
const agent = ref(null);
const sales = ref([]);
const isLoading = ref(false);
const periodParams = ref({});

const brl = value =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);

const formatDate = iso => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const activeSales = computed(() => sales.value.filter(s => s.status === 'active'));
const cancelledSales = computed(() => sales.value.filter(s => s.status === 'cancelled'));
const totalAmount = computed(() =>
  activeSales.value.reduce((sum, s) => sum + (s.amount ?? 0), 0)
);

async function open(agentData, params = {}) {
  agent.value = agentData;
  periodParams.value = params;
  isOpen.value = true;
  sales.value = [];
  isLoading.value = true;
  try {
    const { data } = await SalesAPI.get({ agent_id: agentData.agent_id, ...params });
    sales.value = data;
  } finally {
    isLoading.value = false;
  }
}

function close() {
  isOpen.value = false;
  agent.value = null;
  sales.value = [];
}

function goToConversation(conversationId) {
  close();
  router.push({
    name: 'inbox_conversation',
    params: { accountId: accountId.value, conversation_id: conversationId },
  });
}

defineExpose({ open });
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      @keydown.esc="close"
    >
      <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" @click="close" />

      <div
        class="relative z-10 w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl bg-n-surface-1 border border-n-weak shadow-xl"
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-6 py-4 border-b border-n-weak shrink-0">
          <div>
            <h2 class="text-base font-semibold text-n-slate-12">
              {{ $t('SALES.AGENT_SALES.TITLE', { name: agent?.agent_name }) }}
            </h2>
            <div v-if="!isLoading" class="flex items-center gap-3 mt-0.5">
              <span class="text-xs text-n-slate-10">
                {{ $t('SALES.AGENT_SALES.ACTIVE', { count: activeSales.length }) }}
              </span>
              <span class="text-xs text-n-slate-12 font-medium">{{ brl(totalAmount) }}</span>
              <span v-if="cancelledSales.length" class="text-xs text-n-slate-9">
                {{ $t('SALES.AGENT_SALES.CANCELLED', { count: cancelledSales.length }) }}
              </span>
            </div>
          </div>
          <button
            class="p-1.5 rounded-lg text-n-slate-9 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
            @click="close"
          >
            <span class="i-lucide-x text-lg" />
          </button>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto px-4 py-3">
          <div v-if="isLoading" class="flex justify-center py-12">
            <span class="i-lucide-loader-circle animate-spin text-2xl text-n-slate-9" />
          </div>

          <div v-else-if="!sales.length" class="flex flex-col items-center gap-2 py-12 text-n-slate-9">
            <span class="i-lucide-shopping-bag text-3xl" />
            <p class="text-sm">{{ $t('SALES.AGENT_SALES.EMPTY') }}</p>
          </div>

          <div v-else class="flex flex-col gap-2">
            <div
              v-for="sale in sales"
              :key="sale.id"
              class="rounded-xl border p-4 flex flex-col gap-3"
              :class="
                sale.status === 'active'
                  ? 'border-n-strong bg-n-alpha-3'
                  : 'border-n-weak bg-n-alpha-1 opacity-70'
              "
            >
              <!-- Linha 1: status + valor -->
              <div class="flex items-center justify-between gap-2">
                <span
                  class="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full"
                  :class="
                    sale.status === 'active'
                      ? 'bg-n-alpha-3 text-n-slate-11'
                      : 'bg-n-alpha-2 text-n-slate-9'
                  "
                >
                  <span
                    :class="sale.status === 'active' ? 'i-lucide-check-circle' : 'i-lucide-x-circle'"
                    class="text-[10px]"
                  />
                  {{
                    sale.status === 'active'
                      ? $t('SALES.INFO_PANEL.STATUS_ACTIVE')
                      : $t('SALES.INFO_PANEL.STATUS_CANCELLED')
                  }}
                </span>
                <span
                  class="text-base font-bold"
                  :class="sale.status === 'active' ? 'text-n-slate-12' : 'text-n-slate-9 line-through'"
                >
                  {{ brl(sale.amount) }}
                </span>
              </div>

              <!-- Linha 2: contato + conversa -->
              <div class="flex items-start justify-between gap-4">
                <!-- Contato -->
                <div class="flex items-center gap-2 min-w-0">
                  <span class="i-lucide-user text-sm text-n-slate-9 shrink-0" />
                  <div class="min-w-0">
                    <p class="text-sm font-medium text-n-slate-12 truncate">
                      {{ sale.contact_name || '—' }}
                    </p>
                    <p v-if="sale.contact_phone" class="text-xs text-n-slate-9">
                      {{ sale.contact_phone }}
                    </p>
                  </div>
                </div>

                <!-- Link para conversa -->
                <button
                  v-if="sale.conversation_id"
                  class="flex items-center gap-1.5 text-xs text-n-brand hover:text-n-brand-dark transition-colors shrink-0 mt-0.5"
                  @click="goToConversation(sale.conversation_id)"
                >
                  <span class="i-lucide-message-square text-sm" />
                  {{ $t('SALES.AGENT_SALES.VIEW_CONVERSATION', { id: sale.conversation_id }) }}
                </button>
              </div>

              <!-- Linha 3: pedido + data + tipo -->
              <div class="flex items-center gap-4 text-xs text-n-slate-10">
                <span v-if="sale.order_number" class="font-mono">{{ sale.order_number }}</span>
                <span>{{ formatDate(sale.created_at) }}</span>
                <span v-if="sale.sale_type" class="flex items-center gap-1">
                  <span class="i-lucide-tag text-[10px]" />
                  {{ sale.sale_type }}
                </span>
              </div>

              <!-- Motivo cancelamento -->
              <div
                v-if="sale.status === 'cancelled' && sale.cancellation_reason"
                class="rounded-lg bg-n-alpha-3 border border-n-strong px-3 py-2 text-xs text-n-slate-11"
              >
                <span class="font-medium">{{ $t('SALES.INFO_PANEL.CANCELLATION_REASON') }}:</span>
                {{ sale.cancellation_reason }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
